/**
 * Joining a workspace from an invitation link (?invite=…). The token leaves the address bar at
 * once and is kept only in this tab until the invitation is accepted or dismissed, so a failed
 * request can be retried and the token isn't left in history or shared by copying the address.
 * The invited person reviews exactly what they'd get — workspace, role, permissions, companies and
 * orders — and accepts explicitly. Another account, an expired, revoked or used invitation are
 * shown plainly. No answer (offline, throttled, server error) is kept apart and can be retried:
 * accepting twice returns the same workspace, so a lost answer never joins twice.
 */
import { useCallback, useEffect, useState } from 'react';
import { acceptWorkspaceInvitation, inviteFailure, previewWorkspaceInvitation, type WorkspaceInvitePreview } from '@miraops/contracts';
import { transport } from '../lib/session.ts';
import { useI18n, type MessageKey } from '../lib/i18n.tsx';
import { Banner, Button, Spinner } from '../ui/ui.tsx';
import { Wordmark } from '../ui/icons.tsx';

const KEY = 'miraops.workspaceInvite';

/** Moves ?invite=… from the address bar into this tab's storage. */
export function takeWorkspaceInvite(): string | null {
  const q = new URLSearchParams(window.location.search);
  const fromUrl = q.get('invite');
  if (fromUrl) {
    q.delete('invite');
    const rest = q.toString();
    window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''));
    try { sessionStorage.setItem(KEY, fromUrl); } catch { /* kept in memory only */ }
    return fromUrl;
  }
  try { return sessionStorage.getItem(KEY); } catch { /* ignored: browser storage blocked: no link kept from earlier in this tab */  return null; }
}
export function forgetWorkspaceInvite() { try { sessionStorage.removeItem(KEY); } catch { /* nothing stored */ } }

type View = { kind: 'loading' } | { kind: 'preview'; preview: WorkspaceInvitePreview } | { kind: 'retry' } | { kind: 'accepting' };

export function WorkspaceInvite({ token, email, onJoined, onDone, onSignOut }: {
  token: string; email: string; onJoined: (workspaceId: string) => void; onDone: () => void; onSignOut: () => void;
}) {
  const i18n = useI18n();
  const { t } = i18n;
  const [view, setView] = useState<View>({ kind: 'loading' });
  const [failure, setFailure] = useState<string | null>(null);
  const load = useCallback(async () => {
    setView({ kind: 'loading' });
    try { setView({ kind: 'preview', preview: await previewWorkspaceInvitation(transport, token) }); }
    catch (error) { setView(inviteFailure(error) === 'retry' ? { kind: 'retry' } : { kind: 'preview', preview: { ...EMPTY, state: 'unavailable' } }); }
  }, [token]);
  useEffect(() => { void load(); }, [load]);

  async function accept(preview: WorkspaceInvitePreview) {
    if (!preview.role_id) return;
    setView({ kind: 'accepting' }); setFailure(null);
    try {
      const workspaceId = await acceptWorkspaceInvitation(transport, token, preview.role_id);
      forgetWorkspaceInvite();
      onJoined(workspaceId);
    } catch (error) {
      if (inviteFailure(error) === 'retry') { setFailure(t('wsInvite.noAnswer')); setView({ kind: 'preview', preview }); return; }
      // Refused: read the invitation again to say exactly why (changed, expired, revoked …).
      await load();
    }
  }
  const dismiss = () => { forgetWorkspaceInvite(); onDone(); };
  const p = view.kind === 'preview' ? view.preview : null;

  return (
    <main className="mcpauth" aria-labelledby="ws-invite-title">
      <div className="mcpauth-card">
        <header className="mcpauth-head"><Wordmark /><h1 id="ws-invite-title">{t('wsInvite.title')}</h1></header>
        <div className="mcpauth-body">
          {view.kind === 'loading' || view.kind === 'accepting' ? <div className="center pad"><Spinner label={t(view.kind === 'accepting' ? 'wsInvite.joining' : 'state.loading')} /></div>
          : view.kind === 'retry' ? <>
            <Banner tone="attention">{t('wsInvite.noAnswer')}</Banner>
            <div className="row-actions start"><Button onClick={dismiss}>{t('invite.later')}</Button><Button variant="primary" onClick={() => void load()}>{t('state.retry')}</Button></div>
          </> : p && p.state === 'wrong_account' ? <>
            <Banner tone="attention">{t('wsInvite.wrongAccount', { email })}</Banner>
            <div className="row-actions start"><Button onClick={dismiss}>{t('invite.dismiss')}</Button><Button variant="primary" onClick={onSignOut}>{t('invite.switchAccount')}</Button></div>
          </> : p && p.state !== 'ready' && p.state !== 'accepted' ? <>
            <Banner tone={p.state === 'already_member' ? 'info' : 'danger'}>{t(`wsInvite.state.${p.state}` as MessageKey, { date: p.expires_at ? i18n.date.format(Date.parse(p.expires_at)) : '' })}</Banner>
            {p.state !== 'already_member' && <p className="muted">{t('wsInvite.askAgain')}</p>}
            <div className="row-actions start"><Button variant="primary" onClick={dismiss}>{t('wsInvite.toWorkspaces')}</Button></div>
          </> : p && <>
            <p>{t('wsInvite.from', { name: p.invited_by ?? '—', workspace: p.workspace_name ?? '—' })}</p>
            <dl className="kv ws-invite-scope">
              <dt>{t('wsInvite.role')}</dt><dd><strong>{p.role_name}</strong><span className="muted small"> · {t(`wsInvite.scope.${p.access_scope ?? 'workspace'}` as MessageKey)}</span></dd>
              {p.access_scope === 'companies' && <><dt>{t('wsInvite.companies')}</dt><dd>{p.companies?.length ? p.companies.join(', ') : t('wsInvite.noCompanies')}</dd></>}
              {p.access_scope === 'assigned' && <><dt>{t('wsInvite.orders')}</dt><dd>{t('wsInvite.ordersN', { n: p.order_count ?? 0 })}</dd></>}
              <dt>{t('wsInvite.can')}</dt><dd><ul className="ws-invite-perms">{(p.permissions ?? []).map(code => <li key={code}>{t(`perm.${code}` as MessageKey)}</li>)}</ul></dd>
              <dt>{t('invite.until')}</dt><dd>{p.expires_at ? i18n.date.format(Date.parse(p.expires_at)) : '—'}</dd>
              <dt>{t('invite.account')}</dt><dd>{email}</dd>
            </dl>
            {failure && <Banner tone="attention" action={<Button size="sm" onClick={() => void accept(p)}>{t('state.retry')}</Button>}>{failure}</Banner>}
            <div className="row-actions start">
              <Button onClick={dismiss}>{t('invite.notNow')}</Button>
              <Button variant="primary" onClick={() => void accept(p)}>{t(p.state === 'accepted' ? 'wsInvite.open' : 'wsInvite.accept', { workspace: p.workspace_name ?? '' })}</Button>
            </div>
          </>}
        </div>
      </div>
    </main>
  );
}

const EMPTY: WorkspaceInvitePreview = { state: 'unavailable', workspace_id: null, workspace_name: null, role_id: null, role_name: null, access_scope: null,
  permissions: null, companies: null, order_count: null, invited_by: null, expires_at: null };
